import type { MouseEvent } from "react";
import { Handle, Position } from "@xyflow/react";
import { Box, Typography, Card, CardContent, IconButton, Chip, Stack } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import { CompanyNodeData } from "src/utils/orgChartUtils";
import { CompanyHierarchy } from "src/api/client-api/company/useOrganizationHierarchy";
import { FoodsTooltip } from "@foodpilot/foods";

interface CompanyNodeProps {
    id: string;
    data: CompanyNodeData & {
        onToggleCollapse?: (nodeId: string) => void;
        onNodeClick?: (company: CompanyHierarchy) => void;
    };
    selected?: boolean;
}

export const CompanyNode = ({ id, data, selected }: CompanyNodeProps) => {
    const {
        company,
        name,
        organizationType,
        isRoot,
        childrenCount,
        isCollapsed,
        level,
        onToggleCollapse,
        onNodeClick,
    } = data;

    const handleToggleCollapse = (e: MouseEvent) => {
        e.stopPropagation();
        if (onToggleCollapse) {
            onToggleCollapse(id);
        }
    };

    const handleNodeClick = () => {
        if (onNodeClick) {
            onNodeClick(company);
        }
    };

    const getNodeColor = (isRoot: boolean, organizationType: string) => {
        if (isRoot) return "#1976d2";

        const colorMap: Record<string, string> = {
            Siège: "#1976d2",
            Filiale: "#388e3c",
            Succursale: "#f57c00",
            Division: "#7b1fa2",
            Département: "#d32f2f",
        };

        return colorMap[organizationType] || "#424242";
    };

    const nodeColor = getNodeColor(isRoot, organizationType);

    return (
        <Card
            sx={{
                width: 200,
                minHeight: 120,
                cursor: "pointer",
                border: selected ? `2px solid ${nodeColor}` : "1px solid #e0e0e0",
                boxShadow: selected ? 3 : 1,
                transition: "all 0.2s ease-in-out",
                bgcolor: isRoot ? `${nodeColor}15` : "background.paper",
                "&:hover": {
                    boxShadow: 3,
                    transform: "translateY(-2px)",
                },
            }}
            onClick={handleNodeClick}
        >
            {/* Handle d'entrée (connexions venant du parent) */}
            {!isRoot && (
                <Handle
                    type="target"
                    position={Position.Top}
                    style={{
                        width: 8,
                        height: 8,
                        background: nodeColor,
                        border: "2px solid white",
                    }}
                />
            )}

            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack spacing={1}>
                    {/* En-tête avec icône et nom */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ color: nodeColor }}>
                            {isRoot ?
                                <BusinessIcon fontSize="small" />
                            :   <PersonIcon fontSize="small" />}
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {name}
                        </Typography>
                    </Stack>

                    {/* Type d'organisation */}
                    <Chip
                        label={organizationType}
                        size="small"
                        sx={{
                            bgcolor: `${nodeColor}20`,
                            color: nodeColor,
                            fontSize: "0.7rem",
                            height: 20,
                        }}
                    />

                    {/* Informations supplémentaires */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            Niveau {level}
                        </Typography>

                        {/* Bouton de pliage/dépliage */}
                        {childrenCount > 0 && (
                            <FoodsTooltip
                                title={
                                    isCollapsed ?
                                        `Déplier (${childrenCount} enfants)`
                                    :   `Plier (${childrenCount} enfants)`
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleToggleCollapse}
                                    sx={{
                                        color: nodeColor,
                                        "&:hover": {
                                            bgcolor: `${nodeColor}20`,
                                        },
                                    }}
                                >
                                    {isCollapsed ?
                                        <ChevronRightIcon fontSize="small" />
                                    :   <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                            </FoodsTooltip>
                        )}
                    </Stack>

                    {/* Indicateur d'enfants cachés */}
                    {isCollapsed && childrenCount > 0 && (
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 0.5,
                                bgcolor: `${nodeColor}10`,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="caption" color={nodeColor} sx={{ fontWeight: 500 }}>
                                +{childrenCount} sous-entreprise{childrenCount > 1 ? "s" : ""}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </CardContent>

            {/* Handle de sortie (connexions vers les enfants) */}
            {childrenCount > 0 && !isCollapsed && (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    style={{
                        width: 8,
                        height: 8,
                        background: nodeColor,
                        border: "2px solid white",
                    }}
                />
            )}
        </Card>
    );
};
